import { Loader2, Database } from "lucide-react";
import Pagination from "../common/Pagination";

interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
}

interface Props {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  data: any[];
  columns: Column[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
  totalCount?: number;
}

export default function ReadOnlyTable({
  title, subtitle, icon, data, columns, isLoading, page, totalPages, onPageChange, emptyMessage = "No data found", totalCount
}: Props) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-700 to-brand-700 px-6 py-5 rounded-2xl shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        {subtitle && <p className="text-slate-300 text-sm">{subtitle}</p>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={20}
          onPageChange={(page) => {
            onPageChange?.(page);
          }}
        />
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Database className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">{emptyMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {columns.map((col) => (
                    <th key={col.key} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map((row, i) => (
                  <tr key={i} className="hover:bg-brand-50/20 transition-colors">
                    {columns.map((col, ci) => (
                      <td key={col.key} className={`px-5 py-3 ${ci === 0 ? "font-medium text-gray-800" : "text-gray-600"}`}>
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
